import { useState } from "react";
import { useBilingual } from "@/lib/bilingual";
import { getWeChatId, isWeChatConfigured, copyWeChatId } from "@/lib/wechat";
import { buildWhatsAppUrl, FORWARO_WHATSAPP_NUMBER } from "@/lib/whatsapp";

const ContactDetails = () => {
  const b = useBilingual();
  const [wechatCopied, setWechatCopied] = useState(false);
  const wechatReady = isWeChatConfigured();

  // WeChat has no web deep-link, so the action is copy-the-ID (matches product detail + floating button).
  const handleWeChat = async () => {
    if (!wechatReady) return;
    if (await copyWeChatId()) {
      setWechatCopied(true);
      setTimeout(() => setWechatCopied(false), 2000);
    }
  };

  return (
    <section className="py-12 md:py-20 bg-stone-50">
      <div className="max-w-screen-xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white border border-stone-200 rounded-2xl p-8 flex flex-col gap-5">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-amber-50 text-amber-700">
              <i className="ri-building-2-line text-2xl" />
            </div>
            <div>
              <p className="text-amber-700 text-xs font-semibold tracking-widest uppercase mb-3">
                {b("Headquarters", "公司地址")}
              </p>
              <p className="text-stone-800 text-base font-semibold leading-relaxed mb-1">
                {b("Forwaro Furniture Co., Ltd.", "風華國際有限公司")}
              </p>
              <p className="text-stone-500 text-sm leading-relaxed">
                {b(
                  "Min Tian Industrial Zone, Shatian Town, Dongguan City, Guangdong Province, China",
                  "廣東省東莞市沙田鎮民田工業區",
                )}
              </p>
            </div>
            <a
              href="https://maps.google.com/?q=Shatian+Town+Dongguan+Guangdong+China"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-amber-700 hover:text-amber-600 text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-map-pin-line" />
              {b("View on Google Maps", "在 Google Maps 查看")}
            </a>
          </div>

          <div className="bg-white border border-stone-200 rounded-2xl p-8 flex flex-col gap-5">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-amber-50 text-amber-700">
              <i className="ri-phone-line text-2xl" />
            </div>
            <div>
              <p className="text-amber-700 text-xs font-semibold tracking-widest uppercase mb-3">
                {b("Phone & Fax", "電話與傳真")}
              </p>
              <div className="flex flex-col gap-4">
                <div>
                  <p className="text-stone-400 text-xs uppercase tracking-wide mb-1">
                    {b("Phone", "電話")}
                  </p>
                  <a
                    href="tel:+8676985581169"
                    className="text-stone-800 text-base font-semibold hover:text-amber-700 transition-colors cursor-pointer"
                  >
                    +86 769 8558 1169
                  </a>
                </div>
                <div className="h-px bg-stone-100" />
                <div>
                  <p className="text-stone-400 text-xs uppercase tracking-wide mb-1">
                    {b("Fax", "傳真")}
                  </p>
                  <p className="text-stone-800 text-base font-semibold">+86 769 8868 0628</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white border border-stone-200 rounded-2xl p-8 flex flex-col gap-5">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-amber-50 text-amber-700">
              <i className="ri-mail-line text-2xl" />
            </div>
            <div>
              <p className="text-amber-700 text-xs font-semibold tracking-widest uppercase mb-3">
                {b("Email & Messaging", "電子郵件與訊息")}
              </p>
              <div className="flex flex-col gap-4">
                <div>
                  <p className="text-stone-400 text-xs uppercase tracking-wide mb-1">
                    {b("Email", "電子郵件")}
                  </p>
                  <a
                    href="mailto:pro@forwaro.com"
                    className="text-stone-800 text-base font-semibold hover:text-amber-700 transition-colors cursor-pointer break-all"
                  >
                    pro@forwaro.com
                  </a>
                </div>
                <div className="h-px bg-stone-100" />
                <div>
                  <p className="text-stone-400 text-xs uppercase tracking-wide mb-2">WhatsApp</p>
                  <a
                    href={buildWhatsAppUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2.5 bg-green-600 hover:bg-green-500 text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-whatsapp-line text-base" />
                    {b("Chat on WhatsApp", "開啟 WhatsApp 對話")}
                  </a>
                  <p className="text-stone-400 text-xs mt-2">{FORWARO_WHATSAPP_NUMBER}</p>
                </div>
                <div className="h-px bg-stone-100" />
                <div>
                  <p className="text-stone-400 text-xs uppercase tracking-wide mb-2">WeChat</p>
                  <button
                    type="button"
                    onClick={handleWeChat}
                    title={wechatReady ? getWeChatId() : undefined}
                    className="inline-flex items-center gap-2.5 bg-green-600 hover:bg-green-500 text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className={`${wechatCopied ? "ri-check-line" : "ri-wechat-line"} text-base`} />
                    {wechatCopied
                      ? b("WeChat ID copied", "已複製微信 ID")
                      : b("Chat on WeChat", "開啟微信對話")}
                  </button>
                  <p className="text-stone-400 text-xs mt-2 italic">
                    {wechatReady
                      ? `${b("WeChat ID", "微信 ID")}: ${getWeChatId()}`
                      : b("WeChat ID: to be confirmed", "微信 ID：待 Forwaro 提供")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactDetails;
